import React, { useState } from 'react'
import { Modal, Button, Typography, Avatar, Input, message } from 'antd'
import {
  CloseOutlined,
  EditOutlined,
  LikeOutlined,
  DislikeOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons'
import ReactPlayer from 'react-player'
import axios from 'axios'
import { config } from '../../../../../constants'
import ShortsAccept from '../../../../../assets/accept.png'
import ShortsReject from '../../../../../assets/reject.png'
import ShortsPending from '../../../../../assets/pending.png'
import { useTranslation } from 'react-i18next'

const { Title, Text } = Typography
const { TextArea } = Input

const ShortsPlayer = ({
  defaultQuery,
  isAdmin,
  visible,
  item,
  shlToken,
  onCancelTips,
  onNext,
  onPrev,
  hasNext,
  hasPrev,
  hideButton,
  userData
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectedReason, setRejectedReason] = useState('')
  const [loading, setLoading] = useState(false)

  const isOrgMedia =
    defaultQuery && defaultQuery.isOrg ? defaultQuery.isOrg : false

  // New state for reactions
  const [videoReactions, setVideoReactions] = useState({})
  const [loadingLikes, setLoadingLikes] = useState({})
  const [loadingDislikes, setLoadingDislikes] = useState({})
  const [videoCounts, setVideoCounts] = useState({})

  const API_URL =
    item && item.tip && item.tip.id
      ? `${config.baseURL}library/tip/${item.tip.id}`
      : null

  const { t } = useTranslation()
  // Handle reaction function
  const handleReaction = async (videoId, reaction) => {
    // Check if user has already reacted to this video
    // if (videoReactions[videoId]) {
    //   message.warning(`You have already ${videoReactions[videoId]}d this video`);
    //   return;
    // }

    // Set appropriate loading state
    if (reaction === 'like') {
      setLoadingLikes(prev => ({ ...prev, [videoId]: true }))
    } else {
      setLoadingDislikes(prev => ({ ...prev, [videoId]: true }))
    }

    try {
      const response = await axios.patch(
        `${config.baseURL}library/tip/${videoId}/react`,
        { reaction: reaction },
        {
          headers: {
            Authorization: `Bearer ${shlToken}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.status === 200 && response.data.success) {
        // Update the reaction state to prevent further reactions
        // setVideoReactions(prev => ({
        //   ...prev,
        //   [videoId]: reaction
        // }));

        // Update the counts from API response
        setVideoCounts(prev => ({
          ...prev,
          [videoId]: {
            likes_count: response.data.likes_count,
            dislikes_count: response.data.dislikes_count
          }
        }))

        // message.success(`Successfully ${reaction}d the video!`);
      }
    } catch (error) {
      console.error('Error reacting to video:', error)

      if (error.response?.status === 401) {
        message.error('Authentication failed. Please login again.')
      } else if (error.response?.status === 404) {
        message.error('Video not found.')
      } else if (error.response?.status === 409) {
        message.warning('You have already reacted to this video.')
        // Update state to reflect that user has already reacted
        setVideoReactions(prev => ({
          ...prev,
          [videoId]: reaction
        }))
      } else {
        message.error('Failed to react to video. Please try again.')
      }
    } finally {
      // Clear appropriate loading state
      if (reaction === 'like') {
        setLoadingLikes(prev => ({ ...prev, [videoId]: false }))
      } else {
        setLoadingDislikes(prev => ({ ...prev, [videoId]: false }))
      }
    }
  }

  const handleLike = (e, videoId) => {
    e.stopPropagation() // Prevent card click
    handleReaction(videoId, 'like')
  }

  const handleDislike = (e, videoId) => {
    e.stopPropagation() // Prevent card click
    handleReaction(videoId, 'dislike')
  }

  // Format date from ISO string to readable format
  const formatDate = isoString => {
    if (!isoString) return 'N/A'
    const date = new Date(isoString)
    const day = date
      .getDate()
      .toString()
      .padStart(2, '0')
    const month = date.toLocaleDateString('en-US', { month: 'short' })
    const year = date.getFullYear()
    const hours = date
      .getHours()
      .toString()
      .padStart(2, '0')
    const minutes = date
      .getMinutes()
      .toString()
      .padStart(2, '0')
    return day + ' ' + month + ' ' + year + ' / ' + hours + ':' + minutes
  }

  // Get creator name with fallback
  const getCreatorName = () => {
    if (!item || !item.creator) return 'Unknown'
    return item.creator.name || item.creator.username || 'Unknown'
  }

  // Get status icon based on item status
  const getStatusIcon = () => {
    if (!item || !item.tip || !item.tip.status) return null

    const fontSize = 20 // You can adjust this size as needed
    const status = item.tip.status.toLowerCase()

    if (status === 'approved' || status === 'accept') {
      return (
        <img
          src={ShortsAccept}
          alt="Accepted"
          style={{
            width: fontSize,
            height: fontSize,
            objectFit: 'contain'
          }}
        />
      )
    } else if (status === 'rejected' || status === 'reject') {
      return (
        <img
          src={ShortsReject}
          alt="Rejected"
          style={{
            width: fontSize,
            height: fontSize,
            objectFit: 'contain'
          }}
        />
      )
    } else if (status === 'pending') {
      return (
        <img
          src={ShortsPending}
          alt="Pending"
          style={{
            width: fontSize,
            height: fontSize,
            objectFit: 'contain'
          }}
        />
      )
    }

    return null
  }

  // Get current counts (either from local state or from item)
  const getCurrentCounts = () => {
    const videoId = item?.tip?.id
    if (videoId && videoCounts[videoId]) {
      return videoCounts[videoId]
    }
    return {
      likes_count: item?.tip?.likes_count || 0,
      dislikes_count: item?.tip?.dislikes_count || 0
    }
  }

  const handleClose = () => {
    setIsPlaying(false) // Reset playing state when closing
    setShowRejectModal(false) // Reset reject modal state
    setRejectedReason('') // Clear rejection reason
    onCancelTips() // Call parent's callback to reset state
  }

  const callAPI = async payload => {
    try {
      setLoading(true)
      const response = await fetch(API_URL, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${shlToken}`
        },
        body: JSON.stringify(payload)
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('API call failed:', error)
      message.error('API call failed. Please try again.')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const handleReject = () => {
    setShowRejectModal(true)
  }

  const handleRejectSubmit = async () => {
    if (!rejectedReason.trim()) {
      message.error('Please provide a reason for rejection')
      return
    }

    try {
      const payload = {
        status: 'rejected',
        rejected_reason: rejectedReason.trim()
      }

      await callAPI(payload)
      message.success('Video has been rejected')
      setShowRejectModal(false)
      handleClose() // Use handleClose to properly reset everything
    } catch (error) {
      // Error message is already shown in callAPI
    }
  }

  const handleApprove_ = async () => {
    try {
      const payload = {
        status: 'approved'
      }

      await callAPI(payload)
      message.success('Video approved by Admin')
      handleClose() // Use handleClose to properly reset everything
    } catch (error) {
      // Error message is already shown in callAPI
    }
  }

  const handleApprove = async () => {
    try {
      const payload = {
        status: 'approved'
      }

      await callAPI(payload)

      const indexingPayload = {
        active_organization_id: userData.active_organization_id,
        user_id: userData.id,
        path: item.path,
        file_title: item.title,
        is_shorts: true,
        media_id: item.id
      }

      const response = await axios.post(
        `${config.videoTitleGeneration}`,
        indexingPayload,
        {
          headers: {
            Authorization: `Bearer ${shlToken}`,
            'Content-Type': 'application/json'
          }
        }
      )

      message.success('Video approved by Admin')
      handleClose() // Use handleClose to properly reset everything
    } catch (error) {
      // Error message is already shown in callAPI or handle the new API error
      console.error('Error in approval process:', error)
      message.error('An error occurred during the approval process')
    }
  }

  const handlePlay = () => {
    setIsPlaying(true)
  }

  const handlePause = () => {
    setIsPlaying(false)
  }

  const handleNext = () => {
    if (onNext && hasNext) {
      setIsPlaying(false) // Stop current video
      onNext()
    }
  }

  const handlePrev = () => {
    if (onPrev && hasPrev) {
      setIsPlaying(false) // Stop current video
      onPrev()
    }
  }

  const getVimeoUrl = link => {
    if (!link) {
      return 'https://vimeo.com/1115380355'
    }

    // Match both formats:
    // https://player.vimeo.com/video/1115380355
    // https://vimeo.com/1115380355
    var match = link.match(/vimeo\.com\/(?:video\/)?(\d+)/)
    return match ? 'https://vimeo.com/' + match[1] : link
  }

  const videoId = item?.tip?.id
  const currentCounts = getCurrentCounts()

  return (
    <>
      <Modal
        visible={visible}
        onCancel={handleClose}
        footer={null}
        closable={false}
        width={400}
        bodyStyle={{
          padding: 0,
          borderRadius: '12px',
          overflow: 'hidden'
        }}
        style={{
          top: '50%',
          transform: 'translateY(-50%)'
        }}
      >
        <div
          style={{
            backgroundColor: '#f8f9fa',
            position: 'relative'
          }}
        >
          {/* Header */}
          <div
            style={{
              padding: '8px 20px 8px',
              backgroundColor: 'white',
              borderBottom: '1px solid #f0f0f0',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Text
              style={{
                fontSize: '16px',
                fontWeight: 500,
                color: '#262626'
              }}
            >
              {t('v5:video_upload_request')}
            </Text>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
              >
                <Text
                  style={{
                    fontWeight: '600',
                    fontSize: '12px',
                    color: '#8c8c8c'
                  }}
                >
                  Status:{' '}
                </Text>
                {getStatusIcon()}
              </div>
              {/* <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={handleClose}
                style={{
                  border: 'none',
                  color: '#191717ff'
                }}
              /> */}
            </div>
          </div>

          {/* Video Player Section */}
          <div
            style={{
              backgroundColor: '#e8f4fd',
              height: '440px',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {/* Previous Button */}
            {hasPrev && (
              <div
                onClick={handlePrev}
                style={{
                  position: 'absolute',
                  left: '3px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 1)'
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)'
                }}
              >
                <LeftOutlined style={{ fontSize: '18px', color: '#595959' }} />
              </div>
            )}

            {/* Next Button */}
            {hasNext && (
              <div
                onClick={handleNext}
                style={{
                  position: 'absolute',
                  right: '3px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 10,
                  width: '48px',
                  height: '48px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  border: 'none',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={e => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 1)'
                }}
                onMouseLeave={e => {
                  e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.9)'
                }}
              >
                <RightOutlined style={{ fontSize: '18px', color: '#595959' }} />
              </div>
            )}

            {!isPlaying ? (
              <div
                onClick={handlePlay}
                style={{
                  width: '60px',
                  height: '60px',
                  backgroundColor: 'rgb(117 164 207)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(24, 144, 255, 0.3)'
                }}
              >
                <div
                  style={{
                    width: 0,
                    height: 0,
                    borderLeft: '20px solid white',
                    borderTop: '12px solid transparent',
                    borderBottom: '12px solid transparent',
                    marginLeft: '4px'
                  }}
                />
              </div>
            ) : (
              <ReactPlayer
                url={getVimeoUrl(item && item.link ? item.link : null)}
                playing={isPlaying}
                controls={true}
                width="360px"
                height="400px"
                onPlay={() => setIsPlaying(true)}
                onSeek={seconds => {
                  console.log('Seeked to:', seconds)
                }}
                onProgress={state => {
                  console.log('Progress:', state)
                }}
                onReady={() => {
                  console.log('Player ready')
                }}
                onError={error => {
                  console.error('Player error:', error)
                }}
                config={{
                  file: {
                    attributes: {
                      controlsList: 'nodownload',
                      disablePictureInPicture: true
                    }
                  },
                  vimeo: {
                    playerOptions: {
                      title: false,
                      byline: false,
                      portrait: true,
                      controls: true, // ✅ force Vimeo controls
                      dnt: true // optional: privacy mode
                    }
                  }
                }}
                style={{
                  backgroundColor: '#000',
                  borderRadius: '0'
                }}
              />
            )}
          </div>

          {/* Content Section */}
          <div
            style={{
              backgroundColor: 'white',
              padding: '16px 20px'
            }}
          >
            {/* Title with edit icon and likes/dislikes */}
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                marginBottom: '8px'
              }}
            >
              <Title
                level={5}
                style={{
                  margin: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#262626',
                  lineHeight: '20px',
                  flex: 1,
                  paddingRight: '8px'
                }}
              >
                {item.title || 'Mental Math Hack: Multiply by 11'}
              </Title>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}
              >
                {/* Likes and Dislikes Count with onClick */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <div
                    onClick={e => videoId && handleLike(e, videoId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: videoId ? 'pointer' : 'default',
                      opacity: loadingLikes[videoId] ? 0.6 : 1,
                      pointerEvents:
                        loadingLikes[videoId] || videoReactions[videoId]
                          ? 'none'
                          : 'auto'
                    }}
                  >
                    <LikeOutlined
                      style={{
                        fontSize: '18px',
                        color:
                          videoReactions[videoId] === 'like'
                            ? '#1890ff'
                            : '#52c41a'
                      }}
                    />
                    <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                      {currentCounts.likes_count}
                    </Text>
                  </div>
                  <div
                    onClick={e => videoId && handleDislike(e, videoId)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      cursor: videoId ? 'pointer' : 'default',
                      opacity: loadingDislikes[videoId] ? 0.6 : 1,
                      pointerEvents:
                        loadingDislikes[videoId] || videoReactions[videoId]
                          ? 'none'
                          : 'auto'
                    }}
                  >
                    <DislikeOutlined
                      style={{
                        fontSize: '18px',
                        color:
                          videoReactions[videoId] === 'dislike'
                            ? '#1890ff'
                            : '#ff4d4f'
                      }}
                    />
                    <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                      {currentCounts.dislikes_count}
                    </Text>
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <Text
              style={{
                fontSize: '12px',
                color: '#8c8c8c',
                lineHeight: '18px',
                display: 'block',
                marginBottom: '12px'
              }}
            >
              {item.description
                ? item.description
                    .split('.')
                    .slice(0, 2)
                    .join('.') + '.'
                : 'N/A'}
            </Text>

            {/* Upload info */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '0px'
              }}
            >
              <Text style={{ fontSize: 11, color: '#8c8c8c' }}>
                <Text style={{ fontWeight: '600' }}>Uploaded on: </Text>
                {formatDate(item && item.created_at ? item.created_at : null)}
              </Text>

              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                style={{
                  border: 'none',
                  color: '#8c8c8c',
                  padding: '2px',
                  minWidth: 'auto'
                }}
              />
            </div>

            {/* Uploaded by */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '4px'
              }}
            >
              <Text style={{ fontSize: '11px', color: '#8c8c8c' }}>
                <Text style={{ fontWeight: '600' }}>
                  {t('labels:uploaded_by')}{' '}
                </Text>
                {getCreatorName()}
              </Text>
              <Button
                type="text"
                icon={<EditOutlined />}
                size="small"
                style={{
                  border: 'none',
                  color: '#8c8c8c',
                  padding: '2px',
                  minWidth: 'auto'
                }}
              />
            </div>

            {/* Action buttons */}
            {!hideButton &&
              isOrgMedia &&
              isAdmin &&
              item.tip.status !== 'approved' && (
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    justifyContent: 'flex-end'
                  }}
                >
                  <Button
                    onClick={handleReject}
                    loading={loading}
                    style={{
                      border: 'none',
                      color: '#8c8c8c',
                      backgroundColor: 'transparent',
                      fontWeight: 500,
                      padding: '6px 16px',
                      height: 'auto'
                    }}
                  >
                    {t('v5:reject')}
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleApprove}
                    loading={loading}
                    style={{
                      backgroundColor: '#1890ff',
                      borderColor: '#1890ff',
                      fontWeight: 500,
                      padding: '6px 16px',
                      height: 'auto',
                      borderRadius: '6px'
                    }}
                  >
                    {t('v5:approve')}
                  </Button>
                </div>
              )}
          </div>
        </div>
      </Modal>

      {/* Reject Reason Modal */}
      <Modal
        title={t('v5:reject_video')}
        visible={showRejectModal}
        onCancel={() => setShowRejectModal(false)}
        footer={[
          <Button key="cancel" onClick={() => setShowRejectModal(false)}>
            {t('v4:cancel')}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loading}
            onClick={handleRejectSubmit}
            danger
          >
            {t('v5:reject_video')}
          </Button>
        ]}
        width={400}
      >
        <div style={{ marginBottom: '16px' }}>
          <Text>
            {t('v5:please_provide_a_reason_for_reejection_this_video')}
          </Text>
        </div>
        <TextArea
          placeholder={t('v5:enter_rejection_reason')}
          value={rejectedReason}
          onChange={e => setRejectedReason(e.target.value)}
          rows={4}
          maxLength={500}
          showCount
        />
      </Modal>
    </>
  )
}

export default ShortsPlayer
